import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { ConfigBar } from './ConfigBar'
import type { InitStatus } from './ConfigBar'
import type { ConfigParamKey, KitchensinkConfig } from './config'
import { KitchensinkProvider } from './context'
import type { KitchensinkVariant } from './context'
import type { DeployEnv } from './env'
import { loadSnippetScripts } from './loadSnippet'
import { theme } from './theme'

/** Nav link styling shared by the SPA (NavLink) and the MPA (plain anchors). */
export const navLinkStyle = (isActive: boolean): CSSProperties => ({
  color: isActive ? theme.accent : theme.textMuted,
  textDecoration: 'none',
  fontWeight: isActive ? 600 : 400,
  fontSize: '13px',
  padding: '5px 12px',
  borderRadius: '6px',
  backgroundColor: isActive ? theme.accentSubtle : 'transparent',
  transition: 'all 0.15s',
  display: 'inline-block',
})

type InitedWith = {
  tenantSubdomain: string
  tenantId: string
  env: DeployEnv
}

type Props = {
  variant: KitchensinkVariant
  appTitle: string
  nav: ReactNode
  config: KitchensinkConfig
  onParamChange: (key: ConfigParamKey, value: string) => void
  children: ReactNode
}

export const KitchensinkShell = ({
  variant,
  appTitle,
  nav,
  config,
  onParamChange,
  children,
}: Props) => {
  const [initStatus, setInitStatus] = useState<InitStatus>('idle')
  const [initedWith, setInitedWith] = useState<InitedWith | null>(null)

  const configRef = useRef(config)
  configRef.current = config

  const runInit = async (): Promise<void> => {
    const { env, tenantSubdomain, tenantId } = configRef.current
    setInitStatus('pending')
    try {
      await loadSnippetScripts(env, tenantSubdomain)
      if (!window.ChiliWebData) {
        console.error('[Kitchensink] Initialization failed: snippet scripts not installed')
        setInitStatus('error')
        return
      }
      await window.ChiliWebData.init({ tenantSubdomain, tenantId })
      setInitStatus('done')
      setInitedWith({ tenantSubdomain, tenantId, env })
    } catch (error) {
      console.error('[Kitchensink] Initialization failed:', error)
      setInitStatus('error')
    }
  }

  // Page-load behavior, mirroring a customer site: when the tenant subdomain is already in
  // the URL, install the snippet scripts immediately; with auto-init on, also initialize —
  // on the MPA this means the snippet boots fresh on every full page load, exactly like a
  // real site with the snippet in its HTML.
  useEffect(() => {
    const { env, tenantSubdomain, tenantId, autoInit } = configRef.current
    if (!tenantSubdomain) return
    void loadSnippetScripts(env, tenantSubdomain)
      .then(() => {
        if (autoInit && tenantId) return runInit()
      })
      .catch(error => console.error('[Kitchensink] Failed to load snippet scripts:', error))
    // Intentionally mount-only (reads configRef): script installation is a page-load concern.
  }, [])

  const tenantChangedAfterInit =
    initedWith !== null &&
    (initedWith.tenantSubdomain !== config.tenantSubdomain ||
      initedWith.tenantId !== config.tenantId ||
      initedWith.env !== config.env)

  return (
    <KitchensinkProvider
      value={{
        variant,
        env: config.env,
        tenantSubdomain: config.tenantSubdomain,
        tenantId: config.tenantId,
        isInitialized: initStatus === 'done',
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: theme.bg,
          color: theme.text,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        {/* Header */}
        <header
          style={{
            backgroundColor: theme.surface,
            borderBottom: `1px solid ${theme.border}`,
            padding: '10px 28px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px 32px',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>🌶️</span>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: theme.accent,
                letterSpacing: '-0.01em',
              }}
            >
              {appTitle}
            </span>
          </div>

          <nav style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>{nav}</nav>
        </header>

        <ConfigBar
          config={config}
          initStatus={initStatus}
          tenantChangedAfterInit={tenantChangedAfterInit}
          onParamChange={onParamChange}
          onInit={() => void runInit()}
        />

        {/* Page content */}
        <main style={{ padding: '40px 28px', maxWidth: '760px' }}>{children}</main>
      </div>
    </KitchensinkProvider>
  )
}
